import { LockOpen, Minus, Plus } from 'lucide-react';

// Utility
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { cn } from 'lib/utils';

export default function Product(props) {
    const { onAdd, onRemove, chargesEnabled, currency = 'CAD' } = props;
    const {
        id,
        name,
        description,
        price,
        qty,
        available,
        minPurchase,
        maxPurchase,
        hasCode,
        bundleProductsAvailable,
        originalValue,
        displayDiscountFlag,
    } = props.product;

    const { i18n } = useLingui();

    const soldOut = available <= 0 || minPurchase > available || bundleProductsAvailable === 0; // bundleProductsAvailable will be undefined if not a bundle
    const unavailable = chargesEnabled === 0;

    let quantityText = qty;

    let savingsPercent = originalValue ? Math.round((1 - price / originalValue) * 100) : null;
    const shouldDisplaySavings = displayDiscountFlag === 1;

    if (soldOut) quantityText = t`Sold out`;
    if (unavailable) quantityText = t`Unavailable`;

    const disableAddButton = soldOut || unavailable || qty >= available || qty >= maxPurchase;
    const disableRemoveButton = soldOut || unavailable || qty <= 0;

    return (
        <Card
            className={cn(
                'mb-6 rounded-xl border-solid bg-card p-4 text-card-foreground shadow-none',
                hasCode ? 'border-2 border-primary' : 'border border-border',
            )}
        >
            <div className="mb-2 flex items-center justify-between">
                <h3 className="m-0 text-2xl font-normal text-foreground">{name}</h3>
                {hasCode ? <LockOpen className="ml-2 text-primary" aria-hidden /> : null}
            </div>

            <div className="h-px w-full bg-border" />

            <div className="mt-2 flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="grow">
                    <div>
                        <span className="text-xl font-bold text-foreground">
                            {price > 0
                                ? i18n.number(price, {
                                    style: 'currency',
                                    currency,
                                    currencyDisplay: 'narrowSymbol',
                                })
                                : t`FREE`}
                        </span>
                        {shouldDisplaySavings && savingsPercent !== null && savingsPercent > 0 && (
                            <span className="ml-2 text-xl text-primary">
                                ({t`Save`} {savingsPercent}%)
                            </span>
                        )}
                    </div>

                    <div className="mb-4" />

                    <p className="whitespace-pre-wrap text-muted-foreground">{description}</p>
                </div>

                <div className="mt-8 flex h-10 w-[180px] justify-center self-end md:self-center md:mt-0">
                    {(soldOut || unavailable) && (
                        <div className="flex items-center rounded bg-muted px-8 py-2 text-foreground">
                            {quantityText}
                        </div>
                    )}

                    {!soldOut && !unavailable && (
                        <>
                            <Button
                                type="button"
                                size="icon"
                                disabled={disableRemoveButton}
                                onClick={() => onRemove(id)}
                                aria-label={t`Remove ${name}`}
                                className="mr-2 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:bg-destructive/35 disabled:opacity-100"
                            >
                                <Minus aria-hidden />
                            </Button>

                            <div className="flex items-center rounded border border-solid border-muted-foreground px-8 py-2 text-xl">
                                {quantityText}
                            </div>

                            <Button
                                type="button"
                                size="icon"
                                disabled={disableAddButton}
                                onClick={() => onAdd(id)}
                                aria-label={t`Add ${name}`}
                                className="ml-2 rounded-full bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success)/0.85)] disabled:bg-[hsl(var(--success)/0.35)] disabled:opacity-100"
                            >
                                <Plus aria-hidden />
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </Card>
    );
}
